/**
 * Translations loader.
 *
 * Only English is bundled statically (~227 KB).
 * Other locales are fetched on demand the first time they are needed
 * and cached in memory for the lifetime of the page.
 *
 * Shape returned by `getTranslation(key, lang)`:
 *   resolved string, falling back to English, then the raw key.
 */

import en from "@/assets/locales/en.json";

const localeImporters = {
    fr: () => import("@/assets/locales/fr.json"),
    pt: () => import("@/assets/locales/pt.json"),
    es: () => import("@/assets/locales/es.json"),
    hi: () => import("@/assets/locales/hi.json"),
};

// In-memory cache: { [lang]: { [key]: string } }
const loadedLocales = { en };

// Tracks in-flight loads so we don't double-fetch
const pendingLoads = new Set();

// Subscribers notified when a new locale finishes loading
const subscribers = new Set();

export const subscribeToLocale = (fn) => {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
};

const notifySubscribers = () => subscribers.forEach((fn) => fn());

export const loadLocale = (lang) => {
    if (loadedLocales[lang] || pendingLoads.has(lang) || !localeImporters[lang]) return;
    pendingLoads.add(lang);
    localeImporters[lang]()
        .then((mod) => {
            loadedLocales[lang] = mod.default ?? mod;
            notifySubscribers();
        })
        .catch(() => {
            // On failure leave the slot empty; English fallback continues to work
            pendingLoads.delete(lang);
        });
};

/**
 * Resolve a translation key for the given language.
 * If the locale isn't loaded yet, kicks off the load and returns English fallback.
 */
export const getTranslation = (key, lang) => {
    if (!key) return "";
    const enVal = en[key];
    if (lang === "en") return enVal ?? key;

    const locale = loadedLocales[lang];
    if (!locale) {
        loadLocale(lang);
        return enVal ?? key;
    }
    return locale[key] ?? enVal ?? key;
};

export default { getTranslation, loadLocale, subscribeToLocale };
